import { makeValuesPredicate } from '@sgme/fp';
import type { ProductSelectorTabType } from 'components/shared/ProductAndClientSelector';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { ProductVariation } from 'models/productsReferential';

type WorkspaceLoadingStatus = 'not_loaded' | 'loading' | 'loaded' | 'ready';

type WorkspaceState = Readonly<{
  tabs: string[];
  activeTab?: string;
  status: WorkspaceLoadingStatus;
}>;

export type ActiveQuoteDashboard = 'show' | 'hide';

type DashboardWorkspaceState = WorkspaceState &
  Readonly<{
    activeQuoteDashboard: ActiveQuoteDashboard;
  }>;

const workspaceWithProductSelectorTypes = ['newBatch', 'static', 'dynamic', 'essential'] as const;
export type WorkspaceWithProductSelector = (typeof workspaceWithProductSelectorTypes)[number];
export const isWorkspaceWithProductSelector = makeValuesPredicate(workspaceWithProductSelectorTypes);

export interface ProductSelectorState {
  selectedProductVariation?: Partial<ProductVariation>;
  selectedClient?: Nullable<ISalesCounterpart>;
  selectedProductSelectorTab: ProductSelectorTabType;
  hasSeenDisclaimer: boolean;
  recentQuotedProductVariations: AsyncPayload<readonly ProductVariation[]>;
  selectedAssetTypeFilter: string;
}

export interface EssentialWorkspaceState {
  isTabsPanelShown: boolean;
}

export type WorkspacesStateMap = {
  static: DashboardWorkspaceState & ProductSelectorState;
  dynamic: DashboardWorkspaceState & ProductSelectorState;
  essential: DashboardWorkspaceState & ProductSelectorState & EssentialWorkspaceState;
  newBatch: WorkspaceState & ProductSelectorState;
  ordersBasket: WorkspaceState;
  postTrade: WorkspaceState;
  secondaryMarketQuotes: WorkspaceState;
  secondaryMarketPostEvents: WorkspaceState;
  addonManagement: WorkspaceState;
  smartQuote: WorkspaceState;
};

export const initialWorkspacesState: WorkspacesStateMap = {
  static: {
    tabs: [],
    activeQuoteDashboard: 'hide',
    status: 'not_loaded',
    selectedProductSelectorTab: 'catalogue',
    selectedProductVariation: undefined,
    selectedClient: undefined,
    hasSeenDisclaimer: false,
    recentQuotedProductVariations: { status: 'loading' },
    selectedAssetTypeFilter: 'All',
  },
  dynamic: {
    tabs: [],
    activeQuoteDashboard: 'hide',
    status: 'not_loaded',
    selectedProductSelectorTab: 'catalogue',
    selectedProductVariation: undefined,
    selectedClient: undefined,
    hasSeenDisclaimer: false,
    recentQuotedProductVariations: { status: 'loading' },
    selectedAssetTypeFilter: 'All',
  },
  newBatch: {
    tabs: [],
    status: 'not_loaded',
    selectedProductSelectorTab: 'catalogue',
    selectedProductVariation: undefined,
    selectedClient: undefined,
    hasSeenDisclaimer: false,
    recentQuotedProductVariations: { status: 'loading' },
    selectedAssetTypeFilter: 'All',
  },
  ordersBasket: { tabs: [], status: 'not_loaded' },
  postTrade: { tabs: [], status: 'not_loaded' },
  secondaryMarketQuotes: { tabs: [], status: 'not_loaded' },
  secondaryMarketPostEvents: { tabs: [], status: 'not_loaded' },
  addonManagement: { tabs: [], status: 'not_loaded' },
  essential: {
    tabs: [],
    activeQuoteDashboard: 'hide',
    status: 'not_loaded',
    selectedProductSelectorTab: 'catalogue',
    selectedProductVariation: undefined,
    selectedClient: undefined,
    hasSeenDisclaimer: false,
    recentQuotedProductVariations: { status: 'loading' },
    selectedAssetTypeFilter: 'All',
    isTabsPanelShown: false,
  },
  smartQuote: {
    tabs: [],
    activeTab: undefined,
    status: 'not_loaded',
  },
};
